import React, { useCallback, useRef } from 'react';

import AsyncSelect from 'react-select/async';
import type { CSSObjectWithLabel, GroupBase, StylesConfig } from 'react-select';
import styled from 'styled-components';

import { OdooPartnerKind, useSearchOdooPartnersLazyQuery } from 'data/__generated__';

import { TotemLabel } from './TotemLabel';

/**
 * Champ de rattachement à une société Odoo (client ou fournisseur), servi par une recherche dynamique
 * totadmin → api → Odoo.
 *
 * C'est le seul moyen d'associer une fiche Totem à une fiche Odoo EXISTANTE : l'API ne fait aucun
 * rapprochement automatique au nom. Laissé vide, l'enregistrement crée la société dans Odoo.
 *
 * Les options portent ville et SIRET parce que les homonymes sont la règle dans Odoo (~100 000 tiers) :
 * sans ce contexte, le choix se ferait à l'aveugle.
 */
export type OdooPartnerOption = {
    value: number;
    label: string;
    name: string;
    siret: string | null;
};

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

export const OdooPartnerSelect = ({
    kind,
    label,
    sublabel,
    value,
    onChange,
    dataTest,
    isDisabled = false,
    isInvalid = false,
}: {
    kind: OdooPartnerKind;
    label: string;
    sublabel?: string;
    /** Rattachement courant. Sans le nom (non porté par la fiche Totem), l'id sert d'étiquette. */
    value: number | null;
    onChange: (option: OdooPartnerOption | null) => void;
    dataTest?: string;
    isDisabled?: boolean;
    isInvalid?: boolean;
}) => {
    const [searchOdooPartners] = useSearchOdooPartnersLazyQuery();
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const loadOptions = useCallback(
        (input: string): Promise<OdooPartnerOption[]> =>
            new Promise((resolve) => {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
                const search = input.trim();
                if (search.length < MIN_SEARCH_LENGTH && !/^[1-9]\d*$/.test(search)) {
                    resolve([]);
                    return;
                }
                debounceRef.current = setTimeout(async () => {
                    const { data } = await searchOdooPartners({
                        variables: { search, kind, limit: SEARCH_LIMIT },
                    });
                    resolve(
                        (data?.searchOdooPartners ?? []).map((partner) => ({
                            value: partner.odooPartnerId,
                            name: partner.name,
                            siret: partner.siret ?? null,
                            label: [
                                `${partner.name} (#${partner.odooPartnerId})`,
                                [partner.zipcode, partner.city].filter(Boolean).join(' '),
                                partner.siret ? `SIRET ${partner.siret}` : null,
                                partner.odooCompanyName,
                            ]
                                .filter(Boolean)
                                .join(' — '),
                        })),
                    );
                }, SEARCH_DEBOUNCE_MS);
            }),
        [kind, searchOdooPartners],
    );

    const selectStyles: StylesConfig<OdooPartnerOption, false, GroupBase<OdooPartnerOption>> = {
        control: (base: CSSObjectWithLabel) => ({
            ...base,
            minHeight: 45,
            ...(isInvalid ? { borderColor: '#dc2626' } : {}),
        }),
    };

    return (
        <Container data-test={dataTest}>
            <TotemLabel>{label}</TotemLabel>
            {sublabel ? <Sublabel>{sublabel}</Sublabel> : null}
            <SelectContainer>
                <AsyncSelect<OdooPartnerOption, false>
                    aria-invalid={isInvalid}
                    placeholder="Rechercher par nom ou ID Odoo…"
                    cacheOptions
                    value={value ? { value, label: `Société Odoo #${value}`, name: '', siret: null } : null}
                    onChange={onChange}
                    loadOptions={loadOptions}
                    loadingMessage={() => 'Recherche…'}
                    noOptionsMessage={({ inputValue }) =>
                        inputValue.trim().length < MIN_SEARCH_LENGTH && !/^[1-9]\d*$/.test(inputValue.trim())
                            ? 'Saisissez un ID Odoo ou au moins 3 caractères'
                            : 'Aucune société trouvée'
                    }
                    isClearable
                    isDisabled={isDisabled}
                    styles={selectStyles}
                />
            </SelectContainer>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    color: ${({ theme }) => theme.textColor};
`;

const Sublabel = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;

const SelectContainer = styled.div`
    margin-top: 5px;
    color: #000;
`;
